import React, { useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from 'components/system/layouts/Layout';
import ScreenContext from 'Contexts/Common/ScreenContext';
import { Box, Grid, Table, TableRow, Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import redirectLinks from 'constants/redirectLinks';
import COMMON from 'constants/common';
import MachineConfContentSettingDetail from 'components/machineConf/organisms/contents/MachineConfContentSettingDetail';

import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { ConfListType, ConfMultiType, MachineConfMultiType } from 'types/machineConf/machineConfType';
import NormalButton from 'components/system/atoms/buttons/NormalButton';
import backAllowIcon from 'assets/images/backAllowIcon.svg';
import { setMachineMultiConf, setSelectedStationInfo, setSelectedStationList, setSelectFlg } from 'redux/slices/machineConfSlice';
import { SaveDetailInfoType } from 'types/machineConf/stationSelectType';
import { themeColor } from 'constants/theme';
import HttpConnection from 'utils/httpConnection';
import { REST_API } from 'constants/apiUrls';

import machineMulti from 'assets/images/machineMulti.svg';
import arrowMachineMulti from 'assets/images/arrowMachineMulti.svg';
import arrowMachineMultiLeftToRight from 'assets/images/arrowMachineMultiLeftToRight.svg';
import arrowLeverOffMulti from 'assets/images/arrowLeverOffMulti.svg';
import { getClassificationName } from 'utils/common.helper';
import CLSFCN from 'constants/classification';
import MachineConfContext from 'Contexts/MachineConf/MachineConfContext';
import ConfSaveDialog from 'components/machineConf/atoms/dialog/ConfSaveDialog';
import { InfoDialog } from 'components/system/organisms';
import { InfoDialogProps } from 'components/system/organisms/dialogs/InfoDialog';
import MESSAGES from 'constants/messages';
import MsgHelper from 'utils/message.helper';
import { formatCoinPrice } from 'utils/machineConf.helper';

const CONF_NAME_MAX = 50;
const MSG301: string = MsgHelper.messageFormat(MESSAGES.INF301, '設定');

/* ************ Context ************ */
const ScreenContextValue = {
  title: 'ステーション設定詳細画面',
  screenId: COMMON.SCREEN_ID.STATION_HOME_DETAIL_MULTI,
  currentMenu: COMMON.MENU.MACHINE_CONF,
  disableContents: true,
};

type StationHomeProps = {
  stationName: string;
  giftName: string;
  machineType: string | null;
  machineVersion: string | null;
  position: string | null | undefined;
};

type StyleProps = {
  leftRight?: string;
};

/* ************ Style ************ */
const useStyles = makeStyles<StyleProps>()((_, { leftRight }) => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 12px',
    '& .leftArea': {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '375px',
    },
    '& .centerArea': {
      display: 'flex',
      flexDirection: 'column',
      '& .bottom': {
        display: 'flex',
        marginTop: '5px',
      },
    },
  },
  breakText: {
    whiteSpace: 'break-spaces',
    marginLeft: '5px',
  },
  button: {
    width: '157px',
    height: '70px',
    background: '#EBEBEB',
    margin: '0 0 1px auto',
    fontSize: '20px',
    boxShadow: '5px 5px 3px rgba(0, 0, 0, 0.2)',
    textAlign: 'center',
    lineHeight: '24px',
    padding: '6px 8px',
  },
  emptyButton: {
    width: '157px',
    height: '70px',
  },
  returnButton: {
    width: '157px',
    height: '70px',
    background: '#EBEBEB',
    margin: '0 0 1px auto',
    fontSize: '20px',
    boxShadow: '5px 5px 3px rgba(0, 0, 0, 0.2)',
    padding: 0,
    textAlign: 'left',
    lineHeight: '24px',
    justifyContent: 'flex-start',
  },
  backIconWrap: { marginLeft: '3px' },
  areaContainer: {
    width: '202px',
    height: '381px',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    padding: '8px 4px',
    borderRadius: '4px',
  },
  areaTitle: { fontSize: '23px', lineHeight: '27px' },
  areaMain: { position: 'relative', marginTop: '5px' },
  machineMultiSize: { width: '40px', height: '66px' },
  arrowMachineMultiSize: { width: '44px', height: '55px' },
  machineMultiMarginTop: {
    marginTop: '6px',
  },
  armPowerTitle: {
    fontSize: '14px',
    lineHeight: '19px',
  },
  armPowerBox: {
    marginTop: '6px',
    width: '90px',
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    borderRadius: '3px',
    fontSize: '21px',
    lineHeight: '25px',
    backgroundColor: '#FFF',
  },
  networkBox: {
    marginTop: '8',
    width: '400px',
    height: '33px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    borderRadius: '3px',
    fontSize: '21px',
    lineHeight: '25px',
    backgroundColor: '#FFF',
  },
  armSecondTitle: {
    fontSize: '21px',
    lineHeight: '16px',
    marginLeft: '4px',
  },
  armPowerContainerFirst: {
    marginTop: '5px',
  },
  armPowerContainerSecond: {
    marginTop: '32px',
  },
  armPowerContainerThird: {
    marginTop: '12px',
  },
  armPowerContainerLast: {
    marginTop: '5px',
  },
  arrowMachineMultiLeftToRightContainer: { position: 'absolute', left: '44px' },
  arrowMachineMultiLeftToRightSize: { width: '27px', height: '33px' },
  catcherMovementSpeedContainer: {
    width: '375px',
    height: '182px',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    padding: '3px 23px 10px 10px',
    borderRadius: '4px',
    marginTop: '11px',
  },
  catcherMovementSpeedTitle: {
    fontSize: '22px',
  },
  catcherMovementSpeedTable: {
    width: '194px',
    height: '136px',
  },
  arrowLeverOffMultiContainer: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '144px' },
  arrowLeverOffMultiSize: {
    width: '81px',
    height: '142px',
  },
  detailBlockSpeed: {
    borderCollapse: 'collapse',
    fontSize: '14px',
    display: 'block',
  },
  rowContainer: { padding: '11px', height: '32px' },
  speedTop: {
    backgroundColor: '#EEEEEE',
    width: '97px',
    border: '1px solid #575757',
    textAlign: 'center',
  },
  speedTopZ: {
    color: '#00B050',
    fontSize: '18px',
  },
  speedTopX: {
    color: '#3086ff',
    fontSize: '18px',
  },
  speedTopY: {
    color: '#ff3e3e',
    fontSize: '18px',
  },
  speedTopValue: {
    backgroundColor: '#ffffff',
    border: '1px solid #575757',
    width: '97px',
    textAlign: 'center',
    fontSize: '18px',
  },
  rightBottomContainer: {
    width: '715px',
    height: '93px',
  },
  rightTopContainer: {
    width: '715px',
    height: '468px',
    marginBottom: '10px',
  },
  playAssistContainer: {
    width: '211px',
    height: '293px',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    borderRadius: '4px',
    padding: '13px 8px 5px 13px',
  },
  getSupportContainer: {
    width: '233px',
    height: '293px',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    borderRadius: '4px',
    padding: '13px 8px 5px 13px',
  },
  playAssistTitle: {
    fontSize: '17px',
    lineHeight: '24px',
  },
  playAssistDescription: {
    fontSize: '12px',
    lineHeight: '16px',
  },
  playAssistBox: {
    marginTop: '4px',
    width: '100px',
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    borderRadius: '3px',
    fontSize: '21px',
    lineHeight: '25px',
    backgroundColor: '#FFF',
    whiteSpace: 'nowrap',
  },
  playingDeemedTime: {
    width: 155,
  },
  playAssistLargeBox: {
    marginTop: '4px',
    width: '155px',
    height: '33px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    borderRadius: '3px',
    fontSize: '21px',
    lineHeight: '25px',
    backgroundColor: '#FFF',
  },
  serviceButtonLargeBox : {
    marginTop: '4px',
    width: '215px',
    height: '33px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    borderRadius: '3px',
    fontSize: '17px',
    lineHeight: '25px',
    backgroundColor: '#FFF',
  },
  oneCoinBox: {
    width: '90px',
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    borderRadius: '3px',
    fontSize: '21px',
    lineHeight: '25px',
    marginLeft: '4px',
    backgroundColor: '#FFF',
  },
  pricingBox: {
    marginTop: '14px',
    width: '197px',
    height: '57px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    borderRadius: '3px',
    fontSize: '18px',
    lineHeight: '21px',
    padding: '8px 10px',
    backgroundColor: '#FFF',
  },
  oneCoinLabel: {
    position: 'absolute',
    bottom: '2px',
    right: '-24px',
  },
  playAssistWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  keepCountWrap: {},
  playAssistCount: {
    position: 'absolute',
    bottom: '2px',
    right: '20px',
  },
  playAfterPayout: {
    position: 'absolute',
    bottom: '2px',
    right: '31px',
  },
  helpModeContainer: { margin: '9px 0 5px 0' },
  playTimeContainer: { marginTop: '6px' },
  exchangeKeepTimeContainer: {
    width: '211px',
    height: '76px',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    borderRadius: '4px',
    padding: '6px 25px 5px 25px',
    textAlign: 'center',
    marginTop: '11px',
  },
  keepTimeContainer: {
    width: '233px',
    height: '76px',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    borderRadius: '4px',
    padding: '6px 25px 5px 25px',
    textAlign: 'center',
    marginTop: '11px',
  },
  eMoneyContainer: {
    width: '210px',
    height: '120px',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    borderRadius: '4px',
    padding: '6px 2px 5px 2px',
    textAlign: 'center',
    marginTop: '28px',
    marginLeft: '6px'
  },
  serviceButtonContainer : {
    width: '238px',
    height: '76px',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    borderRadius: '4px',
    padding: '6px 25px 5px 25px',
    textAlign: 'center',
    marginTop: '11px',
  },
  messageSettingContainer: {
    width: '488px',
    height: '93px',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    borderRadius: '4px',
    padding: '6px 25px 5px 25px',
    textAlign: 'center',
    marginTop: '2px',
  },
  networkContainer: {
    width: '211px',
    height: '93px',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    borderRadius: '4px',
    padding: '6px 25px 5px 25px',
    textAlign: 'center',
    marginTop: '2px',
  },
  networkMargin: { marginTop: '12px' },
  networkFormatSize: { whiteSpace: 'nowrap', fontSize: 21, width: '180px' },
  acquisitionSupportContainer: {
    marginLeft: '16px',
  },
  keepCountContainer: { marginTop: '15px' },
  prizePayoutContainer: { marginTop: '20px' },
  keepCountTitle: {
    fontSize: '17px',
    lineHeight: '16px',
  },
  keepCountMargin: {
    marginBottom: '10px',
  },
  priceServiceContainer: {
    width: '238px',
    height: '381px',
    border:
      leftRight === COMMON.LEFT_RIGHT.LEFT
        ? `1px solid ${themeColor.leftDarkColor}`
        : `1px solid ${themeColor.rightDarkColor}`,
    borderRadius: '4px',
    padding: '6px 8px 5px 8px',
  },
  priceServiceTitle: {
    fontSize: '23px',
    lineHeight: '27px',
    textAlign: 'center',
  },
  unitPriceSettingContainer: {
    marginTop: '28px',
  },
  unitPriceSettingTitle: {
    fontSize: '19px',
    lineHeight: '33px',
  },
  pricingContainer: { marginTop: '14px' },
  screenDisplayContainer: { marginTop: '55px' },
  grayBg: {
    backgroundColor: 'rgb(128 128 128 / 50%)',
  },
}));

type MovingSpeedAreaProps = {
  confMulti?: ConfMultiType;
};
type CatcherMovementSpeedProps = {
  confMulti?: ConfMultiType;
};

const MovingSpeedArea: React.FC<React.PropsWithChildren<MovingSpeedAreaProps>> = ({ confMulti }) => {
  const { leftRight } = useContext(MachineConfContext);
  const { classes } = useStyles({ leftRight });
  return (
    <Box className={classes.areaContainer}>
      <Box className={classes.areaTitle}>アーム設定</Box>
      <Box className={classes.areaMain}>
        <Box className={classes.arrowMachineMultiLeftToRightContainer}>
          <img
            src={arrowMachineMultiLeftToRight}
            alt="arrowMachineMultiLeftToRight"
            className={classes.arrowMachineMultiLeftToRightSize}
          />
        </Box>
        <Grid container sx={{
          justifyContent: "space-between",
        }}>
          <Grid item>
            <Box>
              <Grid container direction="column" sx={{
                alignItems: "center",
              }}>
                <Grid item>
                  <Box>
                    <img src={machineMulti} alt="machineMulti" className={classes.machineMultiSize} />
                  </Box>
                </Grid>
                <Grid item>
                  <Box className={classes.machineMultiMarginTop}>
                    <img src={arrowMachineMulti} alt="arrowMachineMulti" className={classes.arrowMachineMultiSize} />
                  </Box>
                </Grid>
                <Grid item>
                  <Box className={classes.machineMultiMarginTop}>
                    <img src={machineMulti} alt="machineMulti" className={classes.machineMultiSize} />
                  </Box>
                </Grid>
                <Grid item>
                  <Box className={classes.machineMultiMarginTop}>
                    <img src={arrowMachineMulti} alt="arrowMachineMulti" className={classes.arrowMachineMultiSize} />
                  </Box>
                </Grid>
                <Grid item>
                  <Box className={classes.machineMultiMarginTop}>
                    <img src={machineMulti} alt="machineMulti" className={classes.machineMultiSize} />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item>
            <Box>
              <Grid container direction="column">
                <Grid item>
                  <Box className={classes.armPowerContainerFirst}>
                    <Box className={classes.armPowerTitle}>移動時のパワー</Box>
                    <Box className={classes.armPowerBox}>{confMulti?.armPowerC ?? COMMON.BLANK1}</Box>
                  </Box>
                </Grid>
                <Grid item>
                  <Box className={classes.armPowerContainerSecond}>
                    <Box className={classes.armPowerTitle}>上昇時のパワー</Box>
                    <Box className={classes.armPowerBox}>{confMulti?.armPowerB ?? COMMON.BLANK1}</Box>
                  </Box>
                </Grid>
                <Grid item>
                  <Box className={classes.armPowerContainerThird}>
                    <Box className={classes.armPowerTitle}>
                      アームパワーが
                      <br />
                      変化を開始する
                      <br />
                      までの時間
                    </Box>
                    <Grid container sx={{
                      alignItems: "flex-end",
                    }}>
                      <Grid item>
                        <Box className={classes.armPowerBox}>{confMulti?.timeToChange ?? COMMON.BLANK1}</Box>
                      </Grid>
                      <Grid item>
                        <Box className={classes.armSecondTitle}>秒</Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item>
                  <Box className={classes.armPowerContainerLast}>
                    <Box className={classes.armPowerTitle}>つかむ時のパワー</Box>
                    <Box className={classes.armPowerBox}>{confMulti?.armPowerA ?? COMMON.BLANK1}</Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const CatcherMovementSpeed: React.FC<React.PropsWithChildren<CatcherMovementSpeedProps>> = ({ confMulti }) => {
  const { leftRight } = useContext(MachineConfContext);
  const { classes } = useStyles({ leftRight });
  return (
    <Box className={classes.catcherMovementSpeedContainer}>
      <Box className={classes.catcherMovementSpeedTitle}>キャッチャー移動速度</Box>
      <Box>
        <Grid container sx={{
          justifyContent: "space-between",
        }}>
          <Grid item>
            <Box className={classes.arrowLeverOffMultiContainer}>
              <img src={arrowLeverOffMulti} alt="arrowLeverOffMulti" className={classes.arrowLeverOffMultiSize} />
            </Box>
          </Grid>
          <Grid item>
            <Box className={classes.catcherMovementSpeedTable}>
              <Table className={classes.detailBlockSpeed}>
                <TableRow className={classes.rowContainer}>
                  <td className={classes.speedTop}>
                    <div className={classes.speedTopX}>X速度</div>
                  </td>
                  <td className={classes.speedTopValue}>{confMulti?.speedX ?? COMMON.BLANK1}</td>
                </TableRow>
                <TableRow className={classes.rowContainer}>
                  <td className={classes.speedTop}>
                    <div className={classes.speedTopY}>Y速度</div>
                  </td>
                  <td className={classes.speedTopValue}>{confMulti?.speedY ?? COMMON.BLANK1}</td>
                </TableRow>
                <TableRow className={classes.rowContainer}>
                  <td className={classes.speedTop}>
                    <div className={classes.speedTopZ}>Z速度</div>
                  </td>
                  <td className={classes.speedTopValue}>{confMulti?.speedZ ?? COMMON.BLANK1}</td>
                </TableRow>
                <TableRow className={classes.rowContainer}>
                  <td className={classes.speedTop}>
                    <div className={classes.speedTopZ}>下降時間</div>
                  </td>
                  <td className={classes.speedTopValue}>{confMulti?.descentTime ?? COMMON.BLANK1}</td>
                </TableRow>
              </Table>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const StationHomeDetailMulti: React.FC<React.PropsWithChildren<unknown>> = () => {
  const navigate = useNavigate();
  const { selectConfId } = useParams<{ selectConfId: string }>();
  const dispatch = useAppDispatch();
  const { machineMultiConf, selectedStation, operationFlg, checkWay } = useAppSelector((state) => state.machineConf);
  const [isSomeSetting, setIsSomeSetting] = React.useState(operationFlg === '1' || checkWay === '0');

  const { stationList } = useAppSelector((state) => state.machineConf.stationSelect);
  const [isStationListScreen, setIsStationListScreen] = React.useState(false);
  const [textButtonBack, setTextButtonBack] = React.useState('ステーション\n一覧');
  const savedConfList: Array<ConfListType> = useAppSelector((state) => state.machineConf.savedConfList);
  const { conf, confMulti } = machineMultiConf;
  const leftRight = useAppSelector((state) => state.machineConf.selectedStation.leftRight);
  const { classes } = useStyles({ leftRight });
  const MachineConfContextValue = { leftRight };
  const isLeft = leftRight === COMMON.LEFT_RIGHT.LEFT;
  const isStationConf = conf?.confDiv === COMMON.CONF_DIV.STATION_CONF;
  const [saveConfName, setSaveConfName] = React.useState('');
  const [stationHomeProps, setStationHomeProps] = React.useState<StationHomeProps>({
    stationName: '',
    giftName: '',
    machineType: null,
    machineVersion: null,
    position: null,
  });
  const [savedConfProps, setSavedConfProps] = React.useState<SaveDetailInfoType>({
    saveName: '',
    saveMachineVersion: null,
    saveMachineType: null,
  });

  const processing = React.useRef(false);
  const [infoDialog, setInfoDialog] = React.useState<InfoDialogProps>({
    open: false,
    msg: '',
  });
  const [infoDialogSaveConf, setInfoDialogSaveConf] = React.useState({
    open: false,
    msg: '',
  });
  const [openSaveConfInfoDialog, setOpenSaveConfInfoDialog] = React.useState(false);

  const [openConfSaveDialog, setOpenConfSaveDialog] = React.useState(false);

  // 選択したステーションの設定情報を取得
  const getSettingDetail = async (confId: number) => {
    if (confId) {
      const http = new HttpConnection({ dispatch });
      const data = await http.post<MachineConfMultiType>(REST_API.MACHINE_CONF.GET_SETTING_DETAIL_MULTI, { confId });
      dispatch(setMachineMultiConf(data));
    }
  };

  // 選択中のステーション情報取得
  const getStationSelected = (configId: number) => {
    if (configId) {
      const station = stationList.find((item) => item.confId === configId);
      if (station) {
        setStationHomeProps({
          giftName: station.giftName ?? COMMON.BLANK,
          stationName: station.stationName ?? COMMON.BLANK,
          machineType: station.machineType,
          machineVersion: station.machineVersion,
          position: station.position,
        });
        if (isStationConf) {
          dispatch(
            setSelectedStationInfo({
              stationName: station.stationName ?? '',
              giftName: station.giftName ?? '',
              leftRight: station.leftRight,
              boardStation: station.boardStation,
              machineType: station.machineType,
              machineVersion: station.machineVersion,
              confId: Number(selectConfId),
              position: station.position,
            }),
          );
        }
      }
    } else {
      setStationHomeProps({
        giftName: selectedStation.giftName ?? COMMON.BLANK,
        stationName: selectedStation.stationName ?? COMMON.BLANK,
        machineType: selectedStation.machineType,
        machineVersion: selectedStation.machineVersion,
        position: selectedStation.position,
      });
    }
  };

  // 選択中の保存設定情報取得
  const getSavedConfInfo = (configId: number) => {
    if (configId) {
      const savedConfInfo = savedConfList.find((item) => Number(item.value) === configId);
      if (savedConfInfo) {
        setSavedConfProps({
          saveName: savedConfInfo.name,
          saveMachineVersion: savedConfInfo.machineVersion,
          saveMachineType: savedConfInfo.machineType,
        });
      }
    } else {
      setSavedConfProps({
        saveName: conf?.saveConfName ?? '',
        saveMachineVersion: conf?.machineVersion ?? null,
        saveMachineType: conf?.machineType ?? null,
      });
    }
  };

  useEffect(() => {
    setIsStationListScreen(isStationConf);
    if (isStationConf) {
      setTextButtonBack('ステーション\n一覧');
    } else {
      setTextButtonBack('保存設定\n一覧');
    }
  }, [isStationConf, selectConfId]);

  useEffect(() => {
    const confId = Number(selectConfId);
    getStationSelected(confId);
    getSettingDetail(confId);
    getSavedConfInfo(confId);
  }, [selectConfId, stationList]);

  const handleClickReturn = () => {
    // state(operationFlg,checkWay)の状況を初期化する
    dispatch(setSelectFlg({ operationFlg: '0', checkWay: '1', isSelectAllSetting: '1' }));
    navigate(isStationConf ? redirectLinks.MCONF_STATION_SELECT : redirectLinks.MCONF_STATION_CONF_SAVED_CONF);
  };

  const handleTransitLedConfScreen = () => {
    navigate(redirectLinks.MCONF_MULTI_CONF_LED, {
      state: isSaveSetting ? savedConfProps : undefined,
    });
  };

  const handleOpenSaveConfirmDialog = () => setOpenConfSaveDialog(true);
  const handleCloseConfSaveDialog = (): void => {
    setOpenConfSaveDialog(false);
  };

  // 設定を保存押下
  const handleOnClickOk = async (e: React.FormEvent) => {
    e.preventDefault();

    // 二重登録防止
    if (processing.current) return;
    processing.current = true;

    if (!saveConfName) {
      setInfoDialog({
        open: true,
        msg: MsgHelper.messageFormat(MESSAGES.INF103, 'タイトル'),
        closeFunc: () => setInfoDialog({ open: false, msg: '' }),
      });
    } else if (saveConfName === 'invalid') {
      setInfoDialog({
        open: true,
        msg: MsgHelper.messageFormat(MESSAGES.INF105),
        closeFunc: () => setInfoDialog({ open: false, msg: '' }),
      });
    } else if (CONF_NAME_MAX < saveConfName.length) {
      // 文字数チェック
      setInfoDialog({
        open: true,
        msg: MsgHelper.messageFormat(MESSAGES.INF111, CONF_NAME_MAX.toString()),
        closeFunc: () => setInfoDialog({ open: false, msg: '' }),
      });
    } else {
      const confId = selectConfId ? Number(selectConfId) : selectedStation.confId;
      const station = stationList.find((item) => item.confId === confId);

      const resultSaveConf = await saveConfMulti({
        saveConfName,
        machineConf: machineMultiConf,
        leftRight: station?.leftRight ?? '',
        boardStation: station?.boardStation ?? '',
        machineType: station?.machineType ?? '',
        machineVersion: station?.machineVersion ?? '',
      });
      if (resultSaveConf && typeof resultSaveConf !== 'number' && !resultSaveConf.success) {
        setInfoDialogSaveConf({
          open: true,
          msg: resultSaveConf.errors[0][0],
        });
      } else {
        setOpenConfSaveDialog(false);
        setOpenSaveConfInfoDialog(true);
        setSaveConfName('');
      }
    }
    // 二重登録防止
    setTimeout(() => {
      // 処理中フラグを下げる
      processing.current = false;
    }, 2000);
  };

  const saveConfMulti = async (dataRequest: {
    saveConfName: string;
    machineConf: MachineConfMultiType;
    leftRight: string;
    boardStation: string;
    machineType: string | null;
    machineVersion: string | null;
  }) => {
    const http = new HttpConnection({ dispatch });
    const result = await http.postSecond(REST_API.MACHINE_CONF.SAVE_CONF_MULTI, dataRequest);
    return result;
  };

  // convert to half using normalize
  function convertToHalf(value: string) {
    return value.normalize('NFKC');
  }

  // 設定保存タイトルの変更
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = convertToHalf(e.target.value);
    const checkSpace = value.trim();
    const spaceLength = value.length;

    if (checkSpace === '' && spaceLength !== 0) {
      setSaveConfName('invalid');
    } else {
      setSaveConfName(e.target.value);
    }
  };

  // ダイアログを閉じる
  const handleCloseInfoDialog = (): void => {
    setOpenSaveConfInfoDialog(false);
    setInfoDialogSaveConf({ open: false, msg: '' });
  };

  const handleClickSaveConf = React.useCallback(() => {
    navigate(redirectLinks.MCONF_STATION_CONF_SAVED_CONF);
  }, []);

  const isSaveSetting = machineMultiConf?.conf?.confDiv === COMMON.CONF_DIV.SAVE_CONF;
  const isDisablePlayAssist = confMulti?.playAssist === COMMON.FLAG.OFF;
  const isDisableGetSupport = confMulti?.getSupport === COMMON.FLAG.OFF;
  const isEMoney = confMulti?.eMoney === COMMON.FLAG.ON;
  const isMoney = confMulti?.eMoney === COMMON.FLAG.OFF;
  const playAssistGrayBgClass = isDisablePlayAssist ? `${classes.grayBg}` : '';
  const getSupportGrayBgClass = isDisableGetSupport ? `${classes.grayBg}` : '';
  const getEMoneyGrayBgClass = isEMoney ? `${classes.grayBg}` : '';
  const getMoneyGrayBgClass = isMoney ? `${classes.grayBg}` : '';

  return (
    <ScreenContext.Provider value={ScreenContextValue}>
      <Layout>
        <MachineConfContext.Provider value={MachineConfContextValue}>
          <MachineConfContentSettingDetail
            isStationListScreen={isStationListScreen}
            isSaveSetting={isSaveSetting}
            isLeft={isLeft}
            stationName={stationHomeProps.stationName}
            giftName={stationHomeProps.giftName}
            machineType={isStationConf ? stationHomeProps.machineType : savedConfProps.saveMachineType}
            machineVersion={stationHomeProps.machineVersion}
            savedConfProps={savedConfProps}
            position={stationHomeProps.position}>
            <Box className={classes.container}>
              <Box>
                <Grid container direction="column" sx={{
                  justifyContent: "space-between",
                }}>
                  <Grid item>
                    <Box className="leftArea">
                      <Box>
                        <Grid container spacing={1} direction="column">
                          <Grid item>
                            <NormalButton className={classes.returnButton} onClick={handleClickReturn}>
                              <img
                                src={backAllowIcon}
                                alt="戻る"
                                width={28}
                                height={28}
                                className={classes.backIconWrap}
                              />
                              <p className={classes.breakText}>{`${textButtonBack}`}</p>
                            </NormalButton>
                          </Grid>
                          <Grid item>
                            {isSaveSetting ? (
                              <Box className={classes.emptyButton} />
                            ) : (
                              <NormalButton className={classes.button} onClick={handleOpenSaveConfirmDialog}>
                                設定を保存
                              </NormalButton>
                            )}
                          </Grid>
                          <Grid item>
                            {isSaveSetting ? (
                              <Box className={classes.emptyButton} />
                            ) : (
                              <NormalButton className={classes.button} onClick={handleClickSaveConf}>
                                保存設定一覧
                              </NormalButton>
                            )}
                          </Grid>
                          <Grid item>
                            <NormalButton className={classes.button} onClick={handleTransitLedConfScreen} disabled={isSomeSetting}>
                              照明演出
                            </NormalButton>
                          </Grid>
                        </Grid>
                      </Box>
                      <MovingSpeedArea confMulti={confMulti} />
                    </Box>
                  </Grid>
                  <Grid item>
                    <Box>
                      <CatcherMovementSpeed confMulti={confMulti} />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              <Box>
                <Grid container direction="column">
                  <Grid item>
                    <Box className={classes.rightTopContainer}>
                      <Grid container>
                        <Grid item>
                          <Box>
                            <Grid container direction="column">
                              <Grid item>
                                <Box className={classes.playAssistContainer}>
                                  <Box className={classes.playAssistTitle}>
                                    連続プレイアシスト
                                    {getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, confMulti?.playAssist ?? '')}
                                  </Box>
                                  <Box className={classes.playAssistDescription}>
                                    連続プレイアシストが発動するまでのプレイ回数※発動時は全パワーが”つかみ時のパワー”となる。
                                  </Box>
                                  <Box className={classes.playAssistWrap}>
                                    <Box className={`${classes.playAssistBox} ${playAssistGrayBgClass}`}>
                                      {confMulti?.playAssistCount ?? COMMON.BLANK1}
                                    </Box>
                                    <Box className={classes.playAssistCount}>
                                      <Box className={classes.armSecondTitle}>回</Box>
                                    </Box>
                                  </Box>
                                  <Box className={classes.helpModeContainer}>
                                    <Box className={classes.playAssistTitle}>
                                      おたすけモード
                                      {getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, confMulti?.freePlay ?? '')}
                                      <br />
                                      （フリープレイ）
                                    </Box>
                                  </Box>
                                  <Box className={classes.playAssistDescription}>
                                  連続プレイアシスト発動後の次プレイからコイン投入が不要
                                  </Box>
                                  <Box className={classes.playTimeContainer}>
                                    <Box className={classes.playAssistTitle}>連続プレイみなし時間</Box>
                                    <Box>
                                      <Grid
                                        container
                                        sx={{
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}>
                                        <Grid item>
                                          <Box
                                            className={`${classes.playAssistBox} ${classes.playingDeemedTime} ${playAssistGrayBgClass}`}>
                                            {confMulti?.playingDeemedTime &&
                                              getClassificationName(
                                                CLSFCN.PLAYING_DEEMED_TIME_GRAND.CATEGORY_CD,
                                                confMulti.playingDeemedTime,
                                              )}
                                          </Box>
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid item className={classes.exchangeKeepTimeContainer}>
                                <Box className={classes.playAssistTitle}>両替キープ時間 </Box>
                                <Box>
                                  <Grid
                                    container
                                    sx={{
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}>
                                    <Grid item>
                                      <Box className={classes.playAssistLargeBox}>
                                        {confMulti?.keepTime &&
                                          getClassificationName(CLSFCN.KEEP_TIME.CATEGORY_CD, confMulti.keepTime)}
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Grid>
                              <Grid item className={classes.exchangeKeepTimeContainer}>
                                <Box className={classes.playAssistTitle}>プレイ時間</Box>
                                <Box>
                                  <Grid
                                    container
                                    sx={{
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}>
                                    <Grid item>
                                      <Box className={classes.playAssistLargeBox}>
                                        {confMulti?.playTime &&
                                          getClassificationName(CLSFCN.PLAY_TIME.CATEGORY_CD, confMulti.playTime)}
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>
                        <Grid item>
                          <Box className={classes.acquisitionSupportContainer}>
                            <Grid container direction="column">
                              <Grid item>
                                <Box className={classes.getSupportContainer}>
                                  <Box className={classes.playAssistTitle}>
                                    獲得サポート
                                    {getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, confMulti?.getSupport ?? '')}
                                  </Box>
                                  <Box className={classes.playAssistDescription}>
                                    獲得サポートが発動するまでのプレイ回数※発動時は全パワーが”つかみ時のパワー”となる。
                                  </Box>
                                  <Box className={classes.playAssistWrap}>
                                    <Box className={`${classes.playAssistBox} ${getSupportGrayBgClass}`}>
                                      {confMulti?.getSupportCount ?? COMMON.BLANK1}
                                    </Box>
                                    <Box className={classes.playAfterPayout}>
                                      <Box className={classes.armSecondTitle}>回</Box>
                                    </Box>
                                  </Box>
                                  <Box className={classes.keepCountContainer}>
                                    <Box className={`${classes.keepCountTitle} ${classes.keepCountMargin}`}>
                                      獲得サポート継続回数
                                    </Box>
                                    <Box className={classes.playAssistWrap}>
                                      <Box className={`${classes.playAssistLargeBox} ${getSupportGrayBgClass}`}>
                                        {confMulti?.keepCount
                                          ? getClassificationName(
                                              CLSFCN.KEEP_COUNT.CATEGORY_CD,
                                              `${confMulti?.keepCount}`,
                                            )
                                          : COMMON.BLANK1}
                                      </Box>
                                    </Box>
                                  </Box>
                                  {isStationConf && (
                                    <Box className={classes.prizePayoutContainer}>
                                      <Box className={`${classes.keepCountTitle} ${classes.keepCountMargin}`}>
                                        景品払出後のプレイ回数
                                      </Box>
                                      <Box className={classes.playAssistWrap}>
                                        <Box className={`${classes.playAssistBox} ${getSupportGrayBgClass}`}>
                                          {confMulti?.playAfterPayout ?? COMMON.BLANK1}
                                        </Box>
                                        <Box className={classes.playAfterPayout}>
                                          <Box className={classes.armSecondTitle}>回</Box>
                                        </Box>
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              </Grid>
                              <Grid item className={classes.keepTimeContainer}>
                                <Box className={classes.playAssistTitle}>高さストップ</Box>
                                <Box>
                                  <Grid
                                    container
                                    sx={{
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}>
                                    <Grid item>
                                      <Box className={classes.playAssistLargeBox}>
                                        {getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, confMulti?.yStop ?? '')}
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Grid>
                              <Grid item className={classes.keepTimeContainer}>
                                <Box className={classes.playAssistTitle}>ペイアウトセンサ </Box>
                                <Box>
                                  <Grid
                                    container
                                    sx={{
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}>
                                    <Grid item>
                                      <Box className={classes.playAssistLargeBox}>
                                        {getClassificationName(
                                          CLSFCN.ON_OFF.CATEGORY_CD,
                                          confMulti?.payoutSensor ?? '',
                                        )}
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>
                        <Grid item>
                          <Box className={classes.acquisitionSupportContainer}>
                            <Box className={classes.priceServiceContainer}>
                              <Box className={classes.priceServiceTitle}>コイン・料金設定 </Box>
                              <Box className={classes.pricingContainer}>
                                <Box className={classes.priceServiceTitle}>料金設定</Box>
                                <Grid
                                  container
                                  sx={{
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}>
                                  <Grid item>
                                    <Box className={classes.pricingBox} style={{backgroundColor: isSomeSetting ? COMMON.bgColor : ''}}>
                                      {confMulti?.pricing &&
                                        getClassificationName(CLSFCN.PRICING_NEW.CATEGORY_CD, confMulti.pricing)}
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                              <Box className={classes.unitPriceSettingContainer}>
                                <Box>
                                  <Grid container sx={{
                                    alignItems: "center",
                                    marginLeft: "9px"
                                  }}>
                                    <Grid item>
                                      <Box className={classes.unitPriceSettingTitle}>1コイン =</Box>
                                    </Grid>
                                    <Grid item>
                                      <Box className={classes.playAssistWrap}>
                                        <Box className={`${classes.oneCoinBox} ${getEMoneyGrayBgClass}`} style={{backgroundColor: isSomeSetting ? COMMON.bgColor : '',}}>{getClassificationName(CLSFCN.COIN_UNIT_PRICE.CATEGORY_CD, confMulti?.cost ?? '')}</Box>
                                        <Box className={classes.oneCoinLabel}>
                                          <Box className={classes.armSecondTitle}>円</Box>
                                        </Box>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Box>
                              <Grid item className={classes.eMoneyContainer} style={{backgroundColor: isSomeSetting ? COMMON.bgColor : '',}}>
                                <Box className={classes.playAssistTitle}>電子マネー専用表示 {getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, confMulti?.eMoney ?? '')}</Box>
                                <Box>
                                  <Box className={classes.unitPriceSettingContainer}>
                                    <Box>
                                      <Grid container sx={{
                                        alignItems: "center",
                                      }}>
                                        <Grid item>
                                          <Box className={classes.unitPriceSettingTitle}>1コイン =</Box>
                                        </Grid>
                                        <Grid item>
                                          <Box className={classes.playAssistWrap}>
                                            <Box className={`${classes.oneCoinBox} ${getMoneyGrayBgClass}`} style={{backgroundColor: isSomeSetting ? 'transparent' : '', border: isSomeSetting ? '1px solid black' : ''}}>{confMulti?.eCost ?? COMMON.BLANK1}</Box>
                                            <Box className={classes.oneCoinLabel}>
                                              <Box className={classes.armSecondTitle}>円</Box>
                                            </Box>
                                          </Box>
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </Box>
                                </Box>
                              </Grid>
                            </Box>
                          </Box>
                        <Grid item className={classes.serviceButtonContainer} style={{ marginLeft: '16px' }}>
                          <Box className={classes.playAssistTitle}>サービスボタン設定 </Box>
                          <Box>
                            <Grid
                              container
                              sx={{
                                alignItems: "center",
                                justifyContent: "center",
                              }}>
                              <Grid item>
                                <Box className={classes.playAssistLargeBox} style={{backgroundColor: isSomeSetting ? COMMON.bgColor : '',}}>
                                  {getClassificationName(
                                    CLSFCN.SERVICE_BUTTON_SETTING_MULTI.CATEGORY_CD,
                                    confMulti?.serviceButton ?? '',
                                  )}
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid item>
                    <Box className={classes.rightBottomContainer}>
                      <Grid
                        container
                        sx={{
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}>
                        <Grid item>
                          <Box className={classes.networkContainer}>
                            <Box className={classes.playAssistTitle}>ネットワーク接続</Box>
                            <Box>
                              <Grid
                                container
                                sx={{
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}>
                                <Grid item>
                                  <Box
                                    className={`${classes.playAssistLargeBox} ${classes.networkMargin} ${classes.networkFormatSize}`} style={{backgroundColor: isSomeSetting ? COMMON.bgColor : '',}}>
                                    {confMulti?.network
                                      ? getClassificationName(CLSFCN.NETWORK.CATEGORY_CD, confMulti?.network)
                                      : COMMON.BLANK1}
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item>
                          <Box className={classes.messageSettingContainer}>
                            <Box className={classes.playAssistTitle}>メッセージ設定 </Box>
                            <Box>
                              <Grid
                                container
                                sx={{
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}>
                                <Grid item>
                                  <Box className={`${classes.networkBox} ${classes.networkMargin}`} style={{backgroundColor: isSomeSetting ? COMMON.bgColor : '',}}>
                                    {confMulti?.message
                                      ? getClassificationName(CLSFCN.MESSAGE_SETTING.CATEGORY_CD, confMulti?.message)
                                      : COMMON.BLANK1}
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </MachineConfContentSettingDetail>
        </MachineConfContext.Provider>
      </Layout>
      <ConfSaveDialog
        open={openConfSaveDialog}
        onOk={handleOnClickOk}
        handleChange={handleChange}
        onCancel={handleCloseConfSaveDialog}
      />
      <InfoDialog open={infoDialog.open} msg={infoDialog.msg} closeFunc={infoDialog.closeFunc} />
      <InfoDialog open={infoDialogSaveConf.open} msg={infoDialogSaveConf.msg} closeFunc={handleCloseInfoDialog} />
      <InfoDialog open={openSaveConfInfoDialog} msg={MSG301} closeFunc={handleCloseInfoDialog} />
    </ScreenContext.Provider>
  );
};

export default StationHomeDetailMulti;
